import PropTypes from "prop-types";
import { Component } from "react";
import { initialize, propTypes } from "redux-form";
import reduxFormSubmitter from "app/utils/form/reduxFormSubmitter";
import { bindActionCreators } from "redux";
import { fetchPrepackageAccommodationsOfDuration } from "app/pages/PrePackage/prePackageOfferActionCreators";
import { clearQuotation } from "app/pages/Booking/Quotation/quotationActionCreators";
import { acceptCookyPolicy } from "app/pages/.shared/CookiePolicyFooter/cookiePolicyActionCreators";
import { clearBooking, updateBooking } from "app/pages/Booking/bookingActionCreators";
import { fields, validate } from "app/pages/PrePackage/rental/rentalSearchEngineSchema";
import RentalSearchEngine from "app/pages/PrePackage/rental/RentalSearchEngine.jsx";
import withRouter from "app/utils/hocs/withRouter";
import { bookingPropTypes } from "app/utils/propTypes";
import {
	getAllAvailableDurations,
	getAvailablePrepackageOffers,
	getDurations,
	getMonths,
	getPrices,
	getQuotationPayload,
	getRentalAccommodations,
	selectBookingDataToHydrate,
} from "app/pages/PrePackage/searchEngineSelectors.js";
import { injectIntl } from "react-intl";
import { OFFER_CONTRACT_TYPES, OFFER_TYPES } from "app/constants";
import { pushTagOnSearchEngineSubmit } from "app/utils/analytics";
import get from "lodash/get";
import {
	finishLoadingBar,
	showLoadingBar,
} from "app/pages/.shared/LoadingBar/loadingBarActionCreators";
import { parseQueries } from "app/utils/routerUtils";
import isEqual from "lodash/isEqual";
import { isProductExpired } from "app/pages/FicheProduit/ficheProduitSelector";
import { buildQuotationQueryParams } from "app/utils/urlDataExtractor";

class RentalSearchEngineContainer extends Component {
	componentWillReceiveProps(nextProps) {
		// Si le produit a changé, il faut réinitialiser ses choix dans le moteur de recherche sinon
		// il voit les prix d'un précédent produit
		if (nextProps.productId !== this.props.productId) {
			nextProps.clearBooking();
		}

		// Faire en sorte que l'appel de fetchPrepackageAccommodationsOfDuration se passe après celui de fetchProduit
		if (!isEqual(this.props.prePackageOffer, nextProps.prePackageOffer)) {
			// On passe les nextProps ici pour s'assurer que c'est bien la dernière version des props
			// contenant les prePackages car sinon ca ne fonctionne pas dans /booking/search
			nextProps.fetchPrepackageAccommodationsOfDuration(
				nextProps.uriToFetchForAO,
				nextProps.booking.duration.value,
				nextProps.booking.offer.type,
				nextProps.booking.offer.contractType
			);

			nextProps.initializeForm(nextProps.formInitialValues);
		}

		// Once product page offers received, booking.offer may be updated. We keep the form synced
		// See fetchFicheProduit call in FicheProduitContainer and BookingSearchContainer
		if (!isEqual(this.props.booking.offer, nextProps.booking.offer)) {
			nextProps.initializeForm(nextProps.formInitialValues);
		}
	}

	componentDidMount() {
		const {
			initializeForm,
			formInitialValues,
			updateBooking,
			productUri,
			location = {},
			clearBooking,
			params = {},
		} = this.props;

		const query = parseQueries(location.search);

		const productUriFromPath = params?.productUri; // fiche produit /:productUri
		const productUriFromQuery = query.productUri; // /booking/search /booking/search?productUri=my-product

		const hasProductChanged =
			(productUriFromPath && productUriFromPath !== productUri) ||
			(productUriFromQuery && productUriFromQuery !== productUri);

		if (hasProductChanged) {
			clearBooking();
		}

		// J'ai du dispather manuellement l'initialisation du formulaire
		// car à cause du server side rendering, le form est à l'état _initialised = true
		// ce qui empêche l'initialisation normal via initiialValues
		initializeForm(formInitialValues);

		// TODO replace history block by another solution
		// history.block(this.routerWillLeave);

		// permet d'avoir les prix lorsque l'on persiste le choix du moteur de recherche
		this.props.fetchPrepackageAccommodationsOfDuration(
			this.props.uriToFetchForAO,
			this.props.booking.duration.value,
			this.props.booking.offer.type,
			this.props.booking.offer.contractType
		);

		updateBooking({
			offer: {
				type: OFFER_TYPES.ACCOMODATION_ONLY,
				contractType: OFFER_CONTRACT_TYPES.RENTAL,
			},
		});
	}

	componentWillUnmount() {
		this.props.finishLoadingBar();
		this.props.acceptCookyPolicy();
	}

	render() {
		let { prePackageOffers = [], updateBooking, booking, ...rest } = this.props;

		return (
			<RentalSearchEngine
				updateBooking={updateBooking}
				prePackageOffers={prePackageOffers}
				booking={booking}
				{...rest}
			/>
		);
	}
}

RentalSearchEngineContainer.propTypes = {
	...propTypes,
	intl: PropTypes.object,
	navigate: PropTypes.shape({ push: PropTypes.func, replace: PropTypes.func }),
	route: PropTypes.object,
	finishLoadingBar: PropTypes.func,
	acceptCookyPolicy: PropTypes.func,
	fetchPrepackageAccommodationsOfDuration: PropTypes.func,
	prePackageOffers: PropTypes.object,
	productId: PropTypes.string,
	mealBasis: PropTypes.string,
	booking: bookingPropTypes,
	updateBooking: PropTypes.func,
	initializeForm: PropTypes.func,
	formInitialValues: PropTypes.object,
};

const beforeValidateForm = () => {
	pushTagOnSearchEngineSubmit();
};

const beforeSubmit = (values, ownsProps, dispatch) => {
	dispatch(showLoadingBar());
};

const onSubmitSuccess = (ownProps, dispatch, values, state) => {
	dispatch(clearQuotation());

	const quotationQueryParams = buildQuotationQueryParams(state);

	ownProps.navigate({
		pathname: "/booking/quote",
		search: `?${quotationQueryParams}`,
	});
};

const mapStateToProps = (state, ownProps) => {
	const quotationPayload = getQuotationPayload(
		ownProps.params.productUri || state.ficheProduit.uri
	)(state);
	const currentBooking = state.booking;

	const selectedQuotationPayloadData = selectBookingDataToHydrate(
		ownProps.params.productUri || state.ficheProduit.uri
	)(state);

	const lastBooking = {
		...currentBooking,
		...selectedQuotationPayloadData,
	};

	const durations = getDurations(state.booking.offer.type || get(quotationPayload, "offer.type"))(
		state
	);
	const rentalAccommodations = getRentalAccommodations(
		state.booking.offer.type || get(quotationPayload, "offer.type")
	)(state);

	// TODO Refacto selector avec arguments
	const pricesPerMonth = getPrices(
		state.booking.offer.type || get(quotationPayload, "offer.type"),
		state.booking.offer.contractType || get(quotationPayload, "offer.contractType")
	)(state);

	// TODO Refacto selector avec arguments
	const months = getMonths(
		state.booking.offer.type || get(quotationPayload, "offer.type"),
		state.booking.offer.contractType || get(quotationPayload, "offer.contractType")
	)(state);

	const formInitialValues = {
		departureDate: lastBooking.departureDate,
		duration: lastBooking.duration.value,
		rentalAccommodation: lastBooking.rentalAccommodation.accommodation,
	};

	const prePackageOffers = getAvailablePrepackageOffers(state);

	const prepackageOfferForAO = prePackageOffers.find(
		offer => get(offer, "pricing.type") === OFFER_TYPES.ACCOMODATION_ONLY
	);

	return {
		uriToFetchForAO: get(prepackageOfferForAO, "pricing.uri"),
		allAvailableDurations: getAllAvailableDurations(state),
		prePackageOffers,
		prePackageOffer: state.prePackageOffer,
		productId: state.ficheProduit.id,
		productUri: state.ficheProduit.uri,
		mealBasis: state.ficheProduit.fromPriceType.mealBasis,
		strategy: state.partner.strategy,
		booking: state.booking,
		prices: pricesPerMonth,
		rentalAccommodations,
		durations,
		months,
		formInitialValues,
		uuid: state.auth.uuid,
		shop: state.shop,
		isDealExpired: isProductExpired(state),
	};
};

const mapDispatchToProps = dispatch => {
	return Object.assign(
		{},
		{
			initializeForm: initializeValues => {
				dispatch(initialize("booking-search-form", initializeValues));
			},
		},
		bindActionCreators(
			{
				updateBooking,
				clearBooking,
				finishLoadingBar,
				acceptCookyPolicy,
				fetchPrepackageAccommodationsOfDuration,
			},
			dispatch
		)
	);
};

const reduxFormSubmitterConfig = {
	form: "booking-search-form",
	fields,
	beforeValidateForm,
	validateForm: validate,
	beforeSubmit,
	onSubmitSuccess,
};

export default injectIntl(
	withRouter(
		reduxFormSubmitter(reduxFormSubmitterConfig, mapStateToProps, mapDispatchToProps)(
			RentalSearchEngineContainer
		)
	)
);
